<template>
    <v-dialog :model-value="modelValue" max-width="560" persistent
              @update:model-value="(v) => emits('update:modelValue', v)">
        <v-card>
            <v-card-title>
                Master Pin
                <span v-if="device" class="text-medium-emphasis text-body-2 ml-2">{{ device.slno }}</span>
            </v-card-title>
            <v-card-text>
                <p class="text-medium-emphasis">
                    Per device 6 digit master pin from the device's factory NV. Used by Gradient
                    service tooling for BLE login and CAN OTA recovery. Recorded server side
                    only; the value is set on the device at the factory and cannot be changed
                    remotely. Customer accounts cannot see this value.
                </p>

                <v-alert v-if="errorMessage" type="error" class="mt-3" closable
                         @click:close="errorMessage = ''">
                    {{ errorMessage }}
                </v-alert>

                <div class="mt-4 d-flex align-center">
                    <v-text-field v-model="pinDisplay" :type="reveal ? 'text' : 'password'"
                                  :loading="busy" density="compact" label="Current pin"
                                  readonly variant="outlined"
                                  :hint="hint" persistent-hint/>
                    <v-btn class="ml-2" :icon="reveal ? 'mdi-eye-off' : 'mdi-eye'"
                           variant="text" :disabled="!pinDisplay" @click="reveal = !reveal"/>
                </div>

                <v-divider class="my-4"/>

                <p class="text-medium-emphasis text-body-2 mb-2">
                    Read the 6 digit master pin off the device (CLI <code>config show factory</code>),
                    then save it here so the auth proof endpoint matches the device.
                </p>
                <v-text-field v-model="masterPinInput" :loading="busy" density="compact"
                              label="6 digit master pin" variant="outlined"
                              maxlength="6" :rules="[v => !v || /^[0-9]{6}$/.test(v) || 'Must be exactly 6 digits.']"
                              hide-details="auto"/>

                <div class="mt-4">
                    <v-btn color="primary" prepend-icon="mdi-eye" variant="tonal"
                           class="mr-2" :loading="busy" @click="onReveal">
                        Reveal
                    </v-btn>
                    <v-btn color="warning" prepend-icon="mdi-content-save" variant="tonal"
                           class="mr-2" :loading="busy"
                           :disabled="!/^[0-9]{6}$/.test(masterPinInput)" @click="onSave">
                        Save
                    </v-btn>
                    <v-btn color="error" prepend-icon="mdi-delete" variant="tonal"
                           :loading="busy" @click="onClear">
                        Clear
                    </v-btn>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer/>
                <v-btn variant="text" @click="onClose">Close</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {ref, watch} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const props = defineProps({
    modelValue: Boolean,
    device: {type: Object, required: true},
});
const emits = defineEmits(['update:modelValue']);
const {showSnackbar} = useSnackbarStore();

const pinDisplay = ref('');
const masterPinInput = ref('');
const reveal = ref(false);
const busy = ref(false);
const errorMessage = ref('');
const defaultHint = 'Click Reveal to load the recorded value, or type the device pin and Save.';
const hint = ref(defaultHint);

watch(() => props.modelValue, (open) => {
    if (open) {
        pinDisplay.value = '';
        masterPinInput.value = '';
        reveal.value = false;
        errorMessage.value = '';
        hint.value = defaultHint;
    }
});

// Server stores the 7 byte form (6 ASCII digits + null) as 14 hex chars.
// Decode back to the human readable 6 digit form when the first 6 bytes
// are printable ASCII and the 7th is null. Fall back to raw hex if the
// stored value does not fit that shape (e.g. legacy 12 byte rows that
// have not been resaved yet).
const decodePin = (hex) => {
    if (!hex || typeof hex !== 'string' || hex.length !== 14) {
        return hex || '';
    }
    const bytes = [];
    for (let i = 0; i < 14; i += 2) bytes.push(parseInt(hex.slice(i, i + 2), 16));
    const trailingNull = bytes[6] === 0;
    const allDigits = bytes.slice(0, 6).every(b => b >= 0x30 && b <= 0x39);
    if (trailingNull && allDigits) {
        return String.fromCharCode(...bytes.slice(0, 6));
    }
    return hex;
};

const onReveal = () => {
    if (!props.device) return;
    busy.value = true;
    errorMessage.value = '';
    send('get', `admin/devices/${props.device.slno}/ble-pin`)
        .then((response) => {
            const pin = response?.data?.data?.ble_pin;
            if (pin) {
                pinDisplay.value = decodePin(pin);
                reveal.value = true;
                hint.value = 'Recorded master pin. Compare to the device CLI value.';
            } else {
                pinDisplay.value = '';
                hint.value = 'No pin recorded. Type the device value and Save.';
            }
        })
        .catch((error) => {
            errorMessage.value = error?.response?.data?.message || 'Could not reveal pin.';
        })
        .finally(() => {
            busy.value = false;
        });
};

const onSave = () => {
    if (!props.device) return;
    if (!/^[0-9]{6}$/.test(masterPinInput.value)) {
        errorMessage.value = 'Master pin must be exactly 6 digits.';
        return;
    }
    busy.value = true;
    errorMessage.value = '';
    send('put', `admin/devices/${props.device.slno}/ble-pin`, {master_pin: masterPinInput.value})
        .then((response) => {
            const pin = response?.data?.data?.ble_pin;
            pinDisplay.value = decodePin(pin || '');
            reveal.value = true;
            hint.value = 'Saved. The auth proof endpoint will now match this value.';
            masterPinInput.value = '';
            showSnackbar(response?.data?.message || 'Master pin saved.');
        })
        .catch((error) => {
            errorMessage.value = error?.response?.data?.message || 'Could not save pin.';
        })
        .finally(() => {
            busy.value = false;
        });
};

const onClear = () => {
    if (!props.device) return;
    const confirmation = prompt(
        'Clearing the recorded pin disables BLE login and CAN OTA recovery for this device on the server side. Type "clear" to confirm.'
    );
    if (confirmation !== 'clear') {
        return;
    }
    busy.value = true;
    errorMessage.value = '';
    send('delete', `admin/devices/${props.device.slno}/ble-pin`)
        .then((response) => {
            pinDisplay.value = '';
            hint.value = 'Pin cleared. Server side auth is disabled until a value is saved.';
            showSnackbar(response?.data?.message || 'Pin cleared.');
        })
        .catch((error) => {
            errorMessage.value = error?.response?.data?.message || 'Could not clear pin.';
        })
        .finally(() => {
            busy.value = false;
        });
};

const onClose = () => {
    emits('update:modelValue', false);
};
</script>
